/**
 * Add-Role Page — flow for adding a second role to an existing account.
 *
 * Steps:
 *  1. Fill role-specific profile form
 *  2. Submit -> backend stores profile + sets role status to `pending_payment`
 *  3. Redirect to /dashboard/subscription?role=<target> to complete payment
 *  4. Saferpay webhook/verify activates the role in `roles[]`
 */
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, ArrowLeft, ArrowRight, ShieldCheck } from "lucide-react";
import { useAuth, API } from "@/context/AppContext";

const useQuery = () => new URLSearchParams(useLocation().search);

const AddRolePage = () => {
  const navigate = useNavigate();
  const query = useQuery();
  const { user } = useAuth();
  const targetRole = (query.get("role") || "provider").toLowerCase();
  const [submitting, setSubmitting] = useState(false);

  const [clientData, setClientData] = useState({
    address: "", city: "", postal_code: "", canton: "",
  });
  const [providerData, setProviderData] = useState({
    company_name: "", categories: "", certifications: "",
    description: "", hourly_rate: "", years_experience: "",
  });

  useEffect(() => {
    if (!user) navigate("/login");
  }, [user]);

  const isProvider = targetRole === "provider";

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = isProvider
        ? {
            role: "provider",
            company_name: providerData.company_name,
            categories: providerData.categories.split(",").map(s => s.trim()).filter(Boolean),
            certifications: providerData.certifications,
            description: providerData.description,
            hourly_rate: providerData.hourly_rate ? Number(providerData.hourly_rate) : null,
            years_experience: providerData.years_experience ? Number(providerData.years_experience) : null,
          }
        : { role: "client", ...clientData };

      await axios.post(`${API}/account/add-role`, payload, { withCredentials: true });
      toast.success("Profile saved — please complete payment to activate");
      navigate(`/dashboard/subscription?role=${targetRole}&adding=1`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save profile");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-8" data-testid="add-role-page">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="add-role-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-2xl font-bold">
            {isProvider ? "Add Expert Profile" : "Add Client Profile"}
          </h1>
        </div>

        <Card className="mb-4 border-blue-200 bg-blue-50/50">
          <CardContent className="p-4 flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-[#0044CC] mt-0.5 flex-shrink-0" />
            <div className="text-sm text-gray-700">
              You&apos;ll keep your existing account and login. After completing this form you&apos;ll be asked to pay{" "}
              <strong>CHF {isProvider ? "179" : "19"}/year</strong> to activate the new role.
              Both roles will live under the same email and you can switch between them anytime.
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              {isProvider ? "Expert details" : "Client details"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-4">
              {isProvider ? (
                <>
                  <div>
                    <Label htmlFor="company_name">Company name *</Label>
                    <Input
                      id="company_name"
                      value={providerData.company_name}
                      onChange={(e) => setProviderData({ ...providerData, company_name: e.target.value })}
                      required
                      data-testid="add-role-company-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="categories">Service categories (comma separated) *</Label>
                    <Input
                      id="categories"
                      value={providerData.categories}
                      onChange={(e) => setProviderData({ ...providerData, categories: e.target.value })}
                      placeholder="finance, legal, consulting"
                      required
                      data-testid="add-role-categories"
                    />
                  </div>
                  <div>
                    <Label htmlFor="certifications">Certifications (comma separated)</Label>
                    <Input
                      id="certifications"
                      value={providerData.certifications}
                      onChange={(e) => setProviderData({ ...providerData, certifications: e.target.value })}
                      data-testid="add-role-certifications"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="hourly_rate">Hourly rate (CHF)</Label>
                      <Input
                        id="hourly_rate" type="number" min="0"
                        value={providerData.hourly_rate}
                        onChange={(e) => setProviderData({ ...providerData, hourly_rate: e.target.value })}
                        data-testid="add-role-hourly-rate"
                      />
                    </div>
                    <div>
                      <Label htmlFor="years_experience">Years experience</Label>
                      <Input
                        id="years_experience" type="number" min="0"
                        value={providerData.years_experience}
                        onChange={(e) => setProviderData({ ...providerData, years_experience: e.target.value })}
                        data-testid="add-role-years"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="description">Short description</Label>
                    <Textarea
                      id="description" rows={3}
                      value={providerData.description}
                      onChange={(e) => setProviderData({ ...providerData, description: e.target.value })}
                      data-testid="add-role-description"
                    />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <Label htmlFor="address">Address</Label>
                    <Input
                      id="address"
                      value={clientData.address}
                      onChange={(e) => setClientData({ ...clientData, address: e.target.value })}
                      data-testid="add-role-address"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="city">City</Label>
                      <Input id="city" value={clientData.city}
                        onChange={(e) => setClientData({ ...clientData, city: e.target.value })}
                        data-testid="add-role-city" />
                    </div>
                    <div>
                      <Label htmlFor="postal_code">Postal code</Label>
                      <Input id="postal_code" value={clientData.postal_code}
                        onChange={(e) => setClientData({ ...clientData, postal_code: e.target.value })}
                        data-testid="add-role-postal" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="canton">Canton</Label>
                    <Input id="canton" value={clientData.canton}
                      onChange={(e) => setClientData({ ...clientData, canton: e.target.value })}
                      data-testid="add-role-canton" />
                  </div>
                </>
              )}

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#0044CC] hover:bg-[#0033AA] text-white h-12 rounded-full font-semibold"
                data-testid="add-role-submit-btn"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    Continue to payment
                    <ArrowRight className="w-4 h-4 ml-1.5" />
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AddRolePage;
